import { useContext, useEffect, useState } from 'react';

import { PhoneCallIcon } from '@selina-finance/ui';
import mixpanel from 'mixpanel-browser';
import { useNavigate } from 'react-router-dom';

import { BookingCalendly } from '@Components/Calendly';
import {
    FinaliseAdvisorState,
    getFinaliseDevice,
    getFinaliseTrackingProperties,
} from '@Shared/helpers/finaliseAnalytics';
import { isWithinFinaliseCallHours } from '@Shared/helpers/finaliseBusinessHours';
import { ApplicationContext } from '@src/app/contexts/Application';
import BackButton from '@src/ui/components/BackButton/BackButton';
import { BookCallButton } from '@src/ui/components/BookCallButton';
import TrustpilotReviews from '@src/ui/components/TrustpilotReviews/TrustpilotReviews';

export const ADVISOR_PHONE_NUMBER = '020 4525 8044';
export const ADVISOR_PHONE_NUMBER_TEL = ADVISOR_PHONE_NUMBER.replace(/\s/g, '');

export interface ContactCardProps {
    onBookCallClick: () => void;
    onMobileBookCallClick: () => void;
    showAdviceCallHeader?: boolean;
}

export const ContactCard = ({
    onBookCallClick,
    onMobileBookCallClick,
}: ContactCardProps) => (
    <div
        className={
            'p-6 rounded-t-2xl rounded-bl-2xl bg-almond-300 flex flex-col items-center lg:w-400 mb-6'
        }
    >
        <div className='mb-2 text-sm'>We’ll call you soon from this number</div>
        <div className='flex flex-col bg-monochrome-100 rounded-lg p-4 w-full items-center justify-center mb-6'>
            <div className='flex items-center justify-center'>
                <PhoneCallIcon />
                <span className='ml-2 text-lg font-bold'>
                    {ADVISOR_PHONE_NUMBER}
                </span>
            </div>
        </div>
        <div className='mb-2 text-sm'>Or if you prefer</div>
        <BookCallButton
            color='primary-flat'
            className='w-full'
            onClick={() =>
                getFinaliseDevice() === 'desktop'
                    ? onBookCallClick()
                    : onMobileBookCallClick()
            }
        />
    </div>
);

const FinaliseView = () => {
    const [showOfferCalendly, setShowOfferCalendly] = useState<boolean>(false);
    const [isInCallHours, setIsInCallHours] = useState(
        isWithinFinaliseCallHours,
    );

    const { applicationId } = useContext(ApplicationContext);
    const navigate = useNavigate();

    const isInHoursCallFlowEnabled = window._env_.ENABLE_FINALISE_IN_HOURS_CALL;

    useEffect(() => {
        if (!isInHoursCallFlowEnabled) return;

        const updateCallHours = () =>
            setIsInCallHours(isWithinFinaliseCallHours());

        updateCallHours();

        const interval = window.setInterval(updateCallHours, 60 * 1000);

        return () => window.clearInterval(interval);
    }, [isInHoursCallFlowEnabled]);

    const successNavLink = '/eligibility/offer/confirmation';

    const showInHoursCallFlow = isInHoursCallFlowEnabled && isInCallHours;
    const advisorState: FinaliseAdvisorState = showInHoursCallFlow
        ? 'in_hours'
        : 'out_of_hours';

    const getTrackingProperties = () => ({
        ...getFinaliseTrackingProperties({
            advisorState,
            applicationId,
        }),
    });

    const trackBookInstead = () => {
        mixpanel.track('tapped_book_instead', getTrackingProperties());
    };

    const handleBookCallClick = () => {
        trackBookInstead();
        setShowOfferCalendly(true);
    };

    const handleMobileBookCallClick = () => {
        trackBookInstead();
        navigate('/eligibility/offer/book-call');
    };

    return (
        <div className='flex flex-col mx-auto max-w-400 w-full lg:max-w-none'>
            <div className='hidden lg:block mb-8'>
                <BackButton />
            </div>
            <div className='px-0 lg:px-44'>
                <div className='lg:flex lg:items-start lg:justify-around lg:mt-20 lg:mb-40'>
                    <div className='flex flex-col items-center mt-5 lg:mt-0 mr-2'>
                        <div className='flex flex-col items-start'>
                            <p className='self-start leading-100 mb-2 font-sora-bold lg:w-auto text-28 lg:text-44 tracking-tighter text-midnight-600'>
                                Nearly there 🎉
                            </p>
                            <p className='leading-100 font-sora-bold mb-6 lg:w-96 text-2xl lg:text-28'>
                                Speak with your advisor to finish your
                                application
                            </p>
                            <ul className='list-disc space-y-4 mb-8 leading-100 pl-5 text-body text-midnight-600'>
                                <li>Make sure this is right for you</li>
                                <li>See how it fits your budget</li>
                                <li>Get expert advice</li>
                            </ul>
                        </div>
                    </div>

                    <ContactCard
                        onBookCallClick={handleBookCallClick}
                        onMobileBookCallClick={handleMobileBookCallClick}
                    />
                </div>
            </div>

            <div className='lg:px-16'>
                <TrustpilotReviews />
            </div>

            {showOfferCalendly && (
                <BookingCalendly
                    successNavLink={successNavLink}
                    onClose={() => setShowOfferCalendly(false)}
                />
            )}
        </div>
    );
};

export default FinaliseView;
